// PrimeReact Checkbox: onChange(e) → e.checked (boolean)
// CheckboxChangeEvent extends FormEvent (primereact/ts-helpers.d.ts:23-30)
// which carries `checked?: boolean`.
// Verified at: apps/web/node_modules/primereact/checkbox/checkbox.d.ts:95-202
//   onChange?(event: CheckboxChangeEvent): void  (line 202)
// and: apps/web/node_modules/primereact/ts-helpers.d.ts:23-30
//   FormEvent { checked?: boolean; ... }         (line 27)

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Checkbox } from 'primereact/checkbox'

interface Props {
  name: string
  label: string
  helpText?: string
  required?: boolean
  disabled?: boolean
}

export function FormCheckbox({ name, label, helpText, required, disabled }: Props): JSX.Element {
  const { control, formState: { errors } } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy = [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      {/* Checkbox layout: [box] label (inline) — different from text fields which have label above */}
      <div className="flex items-center gap-2">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Checkbox
              inputId={id}
              checked={!!field.value}
              onChange={(e) => field.onChange(e.checked)}
              onBlur={field.onBlur}
              disabled={disabled}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={describedBy}
            />
          )}
        />
        <label htmlFor={id} className="text-sm font-medium">
          {label}{required && <span aria-hidden> *</span>}
        </label>
      </div>
      {helpText && <small id={helpId} className="text-gray-500">{helpText}</small>}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">{errorMessage}</small>
      )}
    </div>
  )
}
